import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

export default function CC413({ submitForm, declarationInfo }) {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (declarationInfo) {
      setFormData(normalizeData(declarationInfo));
    }
    setLoading(false);
  }, [declarationInfo]);

  const normalizeData = (data) => {
    const cloned = JSON.parse(JSON.stringify(data));

    // Initialize message
    cloned.message = cloned.message || {
      message_sender: "",
      message_recipient: "",
      preparation_date_and_time: "",
      message_identification: "",
      message_type: "CC413",
    };

    // Initialize import operation
    cloned.import_operation = cloned.import_operation || {
      mrn: "",
      declaration_type: "",
      additional_declaration_type: "",
    };

    // Initialize national declaration
    cloned.national_declaration = cloned.national_declaration || {
      custom_type: "",
      declaration_category: "",
      safety_security_features: false,
    };

    // Initialize customs office, importer, declarant
    cloned.customs_office = cloned.customs_office || { reference_number: "" };
    cloned.importer = cloned.importer || { identification_number: "" };
    cloned.declarant = cloned.declarant || { identification_number: "" };

    // Initialize goods shipment
    const gs = cloned.goods_shipment || {};
    gs.sequence_number = gs.sequence_number || "";
    gs.previous_documents = gs.previous_documents || [];
    gs.supporting_documents = gs.supporting_documents || [];

    // Initialize exporter
    gs.exporter = gs.exporter || {};
    gs.exporter.identification_number = gs.exporter.identification_number || "";
    gs.exporter.name = gs.exporter.name || "";

    // Initialize consignment
    gs.consignment = gs.consignment || {};
    gs.consignment.container_indicator =
      gs.consignment.container_indicator || "0";
    gs.consignment.mode_of_transport_at_the_border =
      gs.consignment.mode_of_transport_at_the_border || "";
    gs.consignment.location_of_goods = gs.consignment.location_of_goods || {};
    gs.consignment.location_of_goods.type_of_location =
      gs.consignment.location_of_goods.type_of_location || "";
    gs.consignment.location_of_goods.qualifier_of_identification =
      gs.consignment.location_of_goods.qualifier_of_identification || "";
    gs.consignment.location_of_goods.customs_office =
      gs.consignment.location_of_goods.customs_office || { reference_number: "" };
    gs.consignment.active_border_transport_means = gs.consignment
      .active_border_transport_means || { nationality: "" };
    gs.consignment.transport_documents =
      gs.consignment.transport_documents || [];

    // Initialize delivery terms
    gs.delivery_terms = gs.delivery_terms || {
      incoterm_code: "",
      unlocode: "",
    };

    // Initialize other shipment fields
    gs.invoice_currency = gs.invoice_currency || "";
    gs.total_amount_invoiced = gs.total_amount_invoiced || "";
    gs.destination = gs.destination || { country_of_destination: "" };
    gs.nature_of_transaction = gs.nature_of_transaction || "";

    // Initialize goods shipment items
    gs.goods_shipment_items = gs.goods_shipment_items || [];
    gs.goods_shipment_items = gs.goods_shipment_items.map((item) => {
      const newItem = { ...item };

      // Procedure
      newItem.procedure = newItem.procedure || {
        requested_procedure: "",
        previous_procedure: "",
        additional_procedures: [],
      };
      newItem.procedure.additional_procedures =
        newItem.procedure.additional_procedures || [];

      // Commodity
      newItem.commodity = newItem.commodity || {};
      newItem.commodity.description_of_goods =
        newItem.commodity.description_of_goods || "";
      newItem.commodity.harmonized_system_subheading_code =
        newItem.commodity.harmonized_system_subheading_code || "";
      newItem.commodity.combined_nomenclature_code =
        newItem.commodity.combined_nomenclature_code || "";
      newItem.commodity.taric_code = newItem.commodity.taric_code || "";
      newItem.commodity.gross_mass = newItem.commodity.gross_mass || "";
      newItem.commodity.net_mass = newItem.commodity.net_mass || "";
      newItem.commodity.supp_unit_amount =
        newItem.commodity.supp_unit_amount || "";
      newItem.commodity.item_amount_invoiced =
        newItem.commodity.item_amount_invoiced || "";
      newItem.commodity.preference = newItem.commodity.preference || "";

      // Origin & Dispatch
      newItem.country_of_dispatch = newItem.country_of_dispatch || {
        country_of_dispatch: "",
      };
      newItem.origin = newItem.origin || { country_of_origin: "" };

      // Packaging
      newItem.packaging = newItem.packaging || [];

      // Customs Valuation & Adjustment
      newItem.customs_valuation = newItem.customs_valuation || {
        valuation_method: "",
      };
      newItem.valuation_adjustment = newItem.valuation_adjustment || {
        valuation_indicators: "",
      };

      return newItem;
    });

    // Initialize evaluation note
    gs.evaluation_note = gs.evaluation_note || { en_for_goods: [] };
    gs.evaluation_note.en_for_goods = gs.evaluation_note.en_for_goods || [];

    cloned.goods_shipment = gs;
    return cloned;
  };

  const handleSubmit = () => {
    if (!reason.trim()) {
      alert("Please provide a reason for the amendment");
      return;
    }

    const requestData = {
      reason: reason,
      declaration_data: formData,
    };

    submitForm({ messageType: "CC413", data: requestData });
  };

  // Update handlers for nested fields
  const updateField = (path, value) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  // Goods Items handlers
  const addGoodsItem = () => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items.push({
        declaration_goods_item_number: (
          newData.goods_shipment.goods_shipment_items.length + 1
        ).toString(),
        procedure: {
          requested_procedure: "",
          previous_procedure: "",
          additional_procedures: [],
        },
        commodity: {
          description_of_goods: "",
          harmonized_system_subheading_code: "",
          combined_nomenclature_code: "",
          taric_code: "",
          gross_mass: "",
          net_mass: "",
          supp_unit_amount: "",
          item_amount_invoiced: "",
          preference: "",
        },
        packaging: [],
        country_of_dispatch: { country_of_dispatch: "" },
        origin: { country_of_origin: "" },
        customs_valuation: { valuation_method: "" },
        valuation_adjustment: { valuation_indicators: "" },
      });
      return newData;
    });
  };

  const removeGoodsItem = (index) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items.splice(index, 1);
      return newData;
    });
  };

  // Additional Procedures handlers
  const addAdditionalProcedure = (itemIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const procedures =
        newData.goods_shipment.goods_shipment_items[itemIndex].procedure
          .additional_procedures;
      procedures.push({
        sequence_number: (procedures.length + 1).toString(),
        additional_procedure: "",
      });
      return newData;
    });
  };

  const removeAdditionalProcedure = (itemIndex, procIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items[
        itemIndex
      ].procedure.additional_procedures.splice(procIndex, 1);
      return newData;
    });
  };

  // Transport Documents handlers
  const addTransportDocument = () => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const docs = newData.goods_shipment.consignment.transport_documents;
      docs.push({
        sequence_number: (docs.length + 1).toString(),
        reference_number: "",
        type: "",
        content: "",
      });
      return newData;
    });
  };

  const removeTransportDocument = (index) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.consignment.transport_documents.splice(index, 1);
      return newData;
    });
  };

  const addShipmentPreviousDocument = () => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const docs = newData.goods_shipment.previous_documents;
      docs.push({
        sequence_number: (docs.length + 1).toString(),
        reference_number: "",
        type: "",
        content: "",
      });
      return newData;
    });
  };

  const removeShipmentPreviousDocument = (index) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.previous_documents.splice(index, 1);
      return newData;
    });
  };

  const addShipmentSupportingDocument = () => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const docs = newData.goods_shipment.supporting_documents;
      docs.push({
        sequence_number: (docs.length + 1).toString(),
        type: "",
        reference_number: "",
        document_line_item_number: "",
        issuing_authority_name: "",
        date_of_validity: "",
        content: "",
      });
      return newData;
    });
  };

  const removeShipmentSupportingDocument = (index) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.supporting_documents.splice(index, 1);
      return newData;
    });
  };

  const addPackaging = (itemIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const packages =
        newData.goods_shipment.goods_shipment_items[itemIndex].packaging;
      packages.push({
        sequence_number: (packages.length + 1).toString(),
        type_of_packages: "",
        number_of_packages: "",
        shipping_marks: "",
      });
      return newData;
    });
  };

  const removePackaging = (itemIndex, packIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items[itemIndex].packaging.splice(
        packIndex,
        1
      );
      return newData;
    });
  };

  const addEvaluationNote = () => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const notes = newData.goods_shipment.evaluation_note.en_for_goods;
      notes.push({
        goods_item_number: "",
        value_typ: "",
        cur_value: "",
        cur_cod: "",
      });
      return newData;
    });
  };

  const removeEvaluationNote = (index) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.evaluation_note.en_for_goods.splice(index, 1);
      return newData;
    });
  };

  if (loading || !formData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div style={{ width: "100%", marginBottom: "20px" }}>
      <Paper
        sx={{ boxShadow: "none" }}
        style={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Declaration Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Sender"
              value={formData.message.message_sender}
              onChange={(e) =>
                updateField("message.message_sender", e.target.value)
              }
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Recipient"
              value={formData.message.message_recipient}
              onChange={(e) =>
                updateField("message.message_recipient", e.target.value)
              }
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              MRN
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {formData.import_operation.mrn || "N/A"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Message Type
            </Typography>
            <Chip label="CC413 - H1" color="primary" />
          </Grid>
        </Grid>
      </Paper>

      {/* Reason */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Reason
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <TextField
          label="Reason for amendment"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          rows={3}
        />
      </Paper>

      {/* Import Operation & National Declaration */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Import Operation & National Declaration
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Declaration Type"
              value={formData.import_operation.declaration_type}
              onChange={(e) =>
                updateField("import_operation.declaration_type", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Additional Declaration Type"
              value={formData.import_operation.additional_declaration_type}
              onChange={(e) =>
                updateField(
                  "import_operation.additional_declaration_type",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Custom Type"
              value={formData.national_declaration.custom_type}
              onChange={(e) =>
                updateField("national_declaration.custom_type", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Declaration Category"
              value={formData.national_declaration.declaration_category}
              onChange={(e) =>
                updateField(
                  "national_declaration.declaration_category",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Safety Security Features"
              value={
                formData.national_declaration.safety_security_features
                  ? "true"
                  : "false"
              }
              onChange={(e) =>
                updateField(
                  "national_declaration.safety_security_features",
                  e.target.value.toLowerCase() === "true"
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Customs Office, Importer, Declarant */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Customs Office
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Customs Office Reference"
              value={formData.customs_office.reference_number}
              onChange={(e) =>
                updateField("customs_office.reference_number", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Importer Identification"
              value={formData.importer.identification_number}
              onChange={(e) =>
                updateField("importer.identification_number", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Declarant Identification"
              value={formData.declarant.identification_number}
              onChange={(e) =>
                updateField("declarant.identification_number", e.target.value)
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Goods Shipment */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Goods Shipment
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Sequence Number"
              value={formData.goods_shipment.sequence_number}
              onChange={(e) =>
                updateField("goods_shipment.sequence_number", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Invoice Currency"
              value={formData.goods_shipment.invoice_currency}
              onChange={(e) =>
                updateField("goods_shipment.invoice_currency", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Total Amount Invoiced"
              value={formData.goods_shipment.total_amount_invoiced}
              onChange={(e) =>
                updateField(
                  "goods_shipment.total_amount_invoiced",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nature Of Transaction"
              value={formData.goods_shipment.nature_of_transaction}
              onChange={(e) =>
                updateField(
                  "goods_shipment.nature_of_transaction",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Country Of Destination"
              value={formData.goods_shipment.destination.country_of_destination}
              onChange={(e) =>
                updateField(
                  "goods_shipment.destination.country_of_destination",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Incoterm Code"
              value={formData.goods_shipment.delivery_terms.incoterm_code}
              onChange={(e) =>
                updateField(
                  "goods_shipment.delivery_terms.incoterm_code",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="UNLOCODE"
              value={formData.goods_shipment.delivery_terms.unlocode}
              onChange={(e) =>
                updateField(
                  "goods_shipment.delivery_terms.unlocode",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider style={{ margin: "20px 0" }} />
        <Typography variant="subtitle1" gutterBottom>
          Exporter Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Exporter Identification Number"
              value={
                formData.goods_shipment.exporter.identification_number || ""
              }
              onChange={(e) =>
                updateField(
                  "goods_shipment.exporter.identification_number",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Exporter Name"
              value={formData.goods_shipment.exporter.name || ""}
              onChange={(e) =>
                updateField("goods_shipment.exporter.name", e.target.value)
              }
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider style={{ margin: "20px 0" }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Previous Documents</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={addShipmentPreviousDocument}
          >
            <AddIcon />
          </IconButton>
        </Box>
        {formData.goods_shipment.previous_documents.map((doc, index) => (
          <Accordion key={index} sx={{ boxShadow: "none" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                Previous Document {doc.sequence_number || index + 1}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Sequence Number"
                    value={doc.sequence_number}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.previous_documents.${index}.sequence_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Reference Number"
                    value={doc.reference_number}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.previous_documents.${index}.reference_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Type"
                    value={doc.type}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.previous_documents.${index}.type`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Content"
                    value={doc.content}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.previous_documents.${index}.content`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeShipmentPreviousDocument(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        <Divider style={{ margin: "20px 0" }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Supporting Documents</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={addShipmentSupportingDocument}
          >
            <AddIcon />
          </IconButton>
        </Box>
        {formData.goods_shipment.supporting_documents.map((doc, index) => (
          <Accordion key={index} sx={{ boxShadow: "none" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                Supporting Document {doc.sequence_number || index + 1}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Sequence Number"
                    value={doc.sequence_number}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.supporting_documents.${index}.sequence_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Type"
                    value={doc.type}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.supporting_documents.${index}.type`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Reference Number"
                    value={doc.reference_number}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.supporting_documents.${index}.reference_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Document Line Item Number"
                    value={doc.document_line_item_number || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.supporting_documents.${index}.document_line_item_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Issuing Authority Name"
                    value={doc.issuing_authority_name || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.supporting_documents.${index}.issuing_authority_name`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Date Of Validity"
                    value={doc.date_of_validity || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.supporting_documents.${index}.date_of_validity`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Content"
                    value={doc.content}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.supporting_documents.${index}.content`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeShipmentSupportingDocument(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        <Divider style={{ margin: "20px 0" }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Evaluation Notes</Typography>
          <IconButton color="primary" size="small" onClick={addEvaluationNote}>
            <AddIcon />
          </IconButton>
        </Box>
        {formData.goods_shipment.evaluation_note.en_for_goods.map(
          (note, index) => (
            <Grid container spacing={2} key={index} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Goods Item Number"
                  value={note.goods_item_number}
                  onChange={(e) =>
                    updateField(
                      `goods_shipment.evaluation_note.en_for_goods.${index}.goods_item_number`,
                      e.target.value
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Value Typ"
                  value={note.value_typ}
                  onChange={(e) =>
                    updateField(
                      `goods_shipment.evaluation_note.en_for_goods.${index}.value_typ`,
                      e.target.value
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Cur Value"
                  value={note.cur_value}
                  onChange={(e) =>
                    updateField(
                      `goods_shipment.evaluation_note.en_for_goods.${index}.cur_value`,
                      e.target.value
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Cur Cod"
                  value={note.cur_cod}
                  onChange={(e) =>
                    updateField(
                      `goods_shipment.evaluation_note.en_for_goods.${index}.cur_cod`,
                      e.target.value
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeEvaluationNote(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          )
        )}
      </Paper>

      {/* Consignment */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Consignment
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Container Indicator"
              value={formData.goods_shipment.consignment.container_indicator}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.container_indicator",
                  e.target.value
                )
              }
              select
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="0">0 - No Container</option>
              <option value="1">1 - Container</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Mode Of Transport At Border"
              value={
                formData.goods_shipment.consignment
                  .mode_of_transport_at_the_border
              }
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.mode_of_transport_at_the_border",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Location Type"
              value={
                formData.goods_shipment.consignment.location_of_goods
                  .type_of_location
              }
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.type_of_location",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Location Qualifier"
              value={
                formData.goods_shipment.consignment.location_of_goods
                  .qualifier_of_identification
              }
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.qualifier_of_identification",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Customs Office Reference"
              value={
                formData.goods_shipment.consignment.location_of_goods
                  .customs_office.reference_number
              }
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.customs_office.reference_number",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Transport Means Nationality"
              value={
                formData.goods_shipment.consignment
                  .active_border_transport_means.nationality
              }
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.active_border_transport_means.nationality",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Transport Documents */}
        <Divider style={{ margin: "15px 0" }} />
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Transport Documents</Typography>
          <IconButton
            color="primary"
            onClick={addTransportDocument}
            size="small"
          >
            <AddIcon />
          </IconButton>
        </Box>
        {formData.goods_shipment.consignment.transport_documents.map(
          (doc, index) => (
            <Accordion key={index} sx={{ boxShadow: "none" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Document {doc.sequence_number || index + 1}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Sequence Number"
                      value={doc.sequence_number}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.consignment.transport_documents.${index}.sequence_number`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Reference Number"
                      value={doc.reference_number}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.consignment.transport_documents.${index}.reference_number`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Type"
                      value={doc.type}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.consignment.transport_documents.${index}.type`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Content"
                      value={doc.content}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.consignment.transport_documents.${index}.content`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" mt={1}>
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => removeTransportDocument(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </AccordionDetails>
            </Accordion>
          )
        )}
      </Paper>

      {/* Goods Shipment Items */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" gutterBottom>
            Goods Shipment Items
          </Typography>
          <IconButton color="primary" onClick={addGoodsItem} size="small">
            <AddIcon />
          </IconButton>
        </Box>
        <Divider style={{ marginBottom: "15px" }} />

        {formData.goods_shipment.goods_shipment_items.map((item, itemIndex) => (
          <Accordion key={itemIndex} sx={{ boxShadow: "none" }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                Goods Item {item.declaration_goods_item_number || itemIndex + 1}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Goods Item Number"
                    value={item.declaration_goods_item_number}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.declaration_goods_item_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Requested Procedure"
                    value={item.procedure.requested_procedure}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.procedure.requested_procedure`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Previous Procedure"
                    value={item.procedure.previous_procedure}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.procedure.previous_procedure`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>

              {/* Additional Procedures */}
              <Divider style={{ margin: "15px 0" }} />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1">
                  Additional Procedures
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() => addAdditionalProcedure(itemIndex)}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Box>
              {item.procedure.additional_procedures.map((proc, procIndex) => (
                <Grid container spacing={2} key={procIndex}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Sequence Number"
                      value={proc.sequence_number}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.goods_shipment_items.${itemIndex}.procedure.additional_procedures.${procIndex}.sequence_number`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Additional Procedure"
                      value={proc.additional_procedure}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.goods_shipment_items.${itemIndex}.procedure.additional_procedures.${procIndex}.additional_procedure`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() =>
                        removeAdditionalProcedure(itemIndex, procIndex)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              {/* Commodity */}
              <Divider style={{ margin: "15px 0" }} />
              <Typography variant="subtitle1">Commodity</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Description Of Goods"
                    value={item.commodity.description_of_goods}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.description_of_goods`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="HS Subheading Code"
                    value={item.commodity.harmonized_system_subheading_code}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.harmonized_system_subheading_code`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Combined Nomenclature Code"
                    value={item.commodity.combined_nomenclature_code}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.combined_nomenclature_code`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Taric Code"
                    value={item.commodity.taric_code}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.taric_code`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Gross Mass"
                    value={item.commodity.gross_mass || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.gross_mass`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Net Mass"
                    value={item.commodity.net_mass || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.net_mass`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Supp Unit Amount"
                    value={item.commodity.supp_unit_amount || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.supp_unit_amount`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Item Amount Invoiced"
                    value={item.commodity.item_amount_invoiced || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.item_amount_invoiced`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Preference"
                    value={item.commodity.preference || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.preference`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Divider style={{ margin: "15px 0" }} />
              <Typography variant="subtitle1">Origin & Dispatch</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country Of Dispatch"
                    value={item.country_of_dispatch?.country_of_dispatch || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.country_of_dispatch.country_of_dispatch`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country Of Origin"
                    value={item.origin?.country_of_origin || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.origin.country_of_origin`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>

              {/* Packaging */}
              <Divider style={{ margin: "15px 0" }} />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1">Packaging</Typography>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => addPackaging(itemIndex)}
                >
                  <AddIcon />
                </IconButton>
              </Box>
              {item.packaging.map((pack, packIndex) => (
                <Grid container spacing={2} key={packIndex} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Sequence Number"
                      value={pack.sequence_number}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.goods_shipment_items.${itemIndex}.packaging.${packIndex}.sequence_number`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Type of Packages"
                      value={pack.type_of_packages || ""}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.goods_shipment_items.${itemIndex}.packaging.${packIndex}.type_of_packages`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Number of Packages"
                      value={pack.number_of_packages}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.goods_shipment_items.${itemIndex}.packaging.${packIndex}.number_of_packages`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Shipping Marks"
                      value={pack.shipping_marks || ""}
                      onChange={(e) =>
                        updateField(
                          `goods_shipment.goods_shipment_items.${itemIndex}.packaging.${packIndex}.shipping_marks`,
                          e.target.value
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removePackaging(itemIndex, packIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}

              {/* Customs Valuation & Adjustment */}
              <Divider style={{ margin: "15px 0" }} />
              <Typography variant="subtitle1">
                Customs Valuation & Adjustment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valuation Method"
                    value={item.customs_valuation?.valuation_method || ""}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.customs_valuation.valuation_method`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valuation Indicators"
                    value={
                      item.valuation_adjustment?.valuation_indicators || ""
                    }
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.valuation_adjustment.valuation_indicators`,
                        e.target.value
                      )
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" mt={2}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeGoodsItem(itemIndex)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      <Box display="flex" gap={2} justifyContent="center" mt={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
        >
          Trimite CC413
        </Button>
      </Box>
    </div>
  );
}
